import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, UploadCloud, Users, Sparkles, ArrowUpRight } from 'lucide-react';
import { JobOffer } from '../../models/job';
import { PublicCvUploadConfig } from './PublicCvUploadConfig';
import { AutoInterviewSettings } from './AutoInterviewSettings';

interface SourcingTabProps {
  job: JobOffer;
  /** Opens the manual CV upload modal. */
  onOpenManualUpload: () => void;
  /** Opens the assign-from-talent-pool side sheet. */
  onOpenTalentPool: () => void;
  /** Opens the Talent Cloud Agent side sheet. */
  onOpenAgent: () => void;
}

type MethodKey = 'manual' | 'pool' | 'agent';

/** Centered icon + title header shared by every card. */
const CardHead: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div className="flex flex-col items-center text-center">
    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-dark-invirtus-soft text-dark-invirtus">
      {icon}
    </span>
    <h6 className="mt-2.5 font-heading text-sm font-semibold text-dark-invirtus">{title}</h6>
  </div>
);

/**
 * Sourcing tab: four equally-sized cards for the ways to bring candidates into a
 * job. "Application Link" shows its shareable link + per-channel tags inline
 * (always on); the other three are launcher cards. Everything is centered; all
 * cards share one height. The per-source breakdown and auto-interview settings
 * live outside this tab.
 */
export const SourcingTab: React.FC<SourcingTabProps> = ({
  job,
  onOpenManualUpload,
  onOpenTalentPool,
  onOpenAgent,
}) => {
  const { t } = useTranslation('job');

  const launchers: { key: MethodKey; icon: React.ReactNode; onClick: () => void }[] = [
    { key: 'manual', icon: <UploadCloud className="h-5 w-5" />, onClick: onOpenManualUpload },
    { key: 'pool', icon: <Users className="h-5 w-5" />, onClick: onOpenTalentPool },
    { key: 'agent', icon: <Sparkles className="h-5 w-5" />, onClick: onOpenAgent },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background-gray">
      {/* Extra bottom padding keeps the last row (the auto-schedule bar) clear of
          the floating chatbot bubble in the bottom-right corner. */}
      <div className="space-y-5 px-4 py-6 pb-24 lg:px-8 lg:py-8 lg:pb-28">
        <div>
          <h5 className="font-heading text-base font-semibold text-dark-invirtus">
            {t('sourcing.methods_heading')}
          </h5>
          <p className="mt-1 font-body text-sm text-dark-invirtus/60">
            {t('sourcing.methods_subheading')}
          </p>
        </div>

        <div className="grid auto-rows-fr grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Application Link — link + channel tags shown inline (always on). */}
          <div className="flex min-h-56 flex-col justify-center rounded-xl border border-light-lila/60 bg-light p-5">
            <CardHead icon={<Link2/>} title={t('sourcing.method_link_title')} />
            <p className="mx-auto mt-2 max-w-md text-center font-body text-xs leading-relaxed text-dark-invirtus/60">
              {t('sourcing.method_link_desc')}
            </p>
            <div className="mt-4">
              <PublicCvUploadConfig job={job} />
            </div>
          </div>

          {/* The other three methods — launcher cards. */}
          {launchers.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={m.onClick}
              className="group flex min-h-56 flex-col items-center justify-center rounded-xl border border-light-lila/60 bg-light p-5 text-center transition-all hover:border-purple/40 hover:shadow-[0_6px_20px_-8px_rgba(16,24,40,0.15)] focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/40"
            >
              <CardHead icon={m.icon} title={t(`sourcing.method_${m.key}_title`)} />
              <p className="mt-2 max-w-xs font-body text-xs leading-relaxed text-dark-invirtus/60">
                {t(`sourcing.method_${m.key}_desc`)}
              </p>
              <span className="mt-4 inline-flex items-center gap-1 font-body text-xs font-medium text-dark-invirtus/55 transition-colors group-hover:text-dark-invirtus">
                {t(`sourcing.method_${m.key}_cta`)}
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </button>
          ))}
        </div>

        {/* Auto-schedule interviews — job-level, applies to any scored candidate. */}
        <AutoInterviewSettings job={job} />
      </div>
    </div>
  );
};

export default SourcingTab;
